// ANA-R3-GEOM-02 — interaction state machine for the shared PolygonEditor.
//
// Headless-ish (GEOM-02 design note): all geometry + interaction logic lives
// here; the Konva render layer (./PolygonEditor.tsx) is a thin view over the
// state this hook exposes. The Tiles editor (small canvas) and the Viewer (OSD
// overlay) share this hook with different `CoordinateTransform`s.
//
// CONTROLLED. The hook is fully controlled: `value` in, `onChange`/`onCommit`
// out. It performs NO I/O, NO network, NO persistence — pure UI state. Drafts
// (an in-progress draw, a vertex mid-drag) live in local state and only reach
// the host through `onChange`; `onCommit` fires on close / draw-done / explicit
// host save.

import { useCallback, useMemo, useRef, useState } from "react";
import type {
  CoordinateTransform,
  ScreenPoint,
} from "./CoordinateTransform.js";
import {
  type EditorMode,
  type Point,
  type PolygonValue,
  distanceSq,
  insertVertex,
  isClosedMode,
  isSelfIntersecting,
  minVertices,
  moveVertex,
  nearestEdge,
  removeVertex,
  resizeRect,
} from "./geometry.js";

/** Handle hit/render radius in SCREEN pixels (constant at any zoom — GEOM-02
 *  AC3). A click within `HANDLE_HIT_RADIUS_PX` of a vertex grabs it; an edge
 *  click within `EDGE_HIT_RADIUS_PX` inserts a vertex. */
export const HANDLE_HIT_RADIUS_PX = 8;
export const EDGE_HIT_RADIUS_PX = 6;
/** Distance (screen px) from the first vertex within which a click closes a
 *  polygon during draw. */
export const CLOSE_SNAP_RADIUS_PX = 10;

export interface UsePolygonEditorOptions {
  /** Controlled value. `mode` + distinct vertices (no closing duplicate). */
  value: PolygonValue;
  /** Host↔screen transform (OSD or canvas). Re-read when its `version` bumps. */
  transform: CoordinateTransform;
  /** Fires on every geometry change (draft moves included). */
  onChange?: (value: PolygonValue) => void;
  /** Fires on close / draw-done / explicit host save (a committed value). */
  onCommit?: (value: PolygonValue) => void;
  /** When false, the editor is render-only (no draw/edit interactions). */
  editable?: boolean;
  /**
   * ANA-R4-VALID-01 — called when a finish (close / Enter / right-click / double-
   * click) is REFUSED because the geometry self-intersects. The draw stays open
   * so the user can fix it; the host shows a "crossing lines — prohibited" popup.
   */
  onInvalidGeometry?: (reason: "self-intersecting") => void;
}

/** What the render layer needs to draw the current state. */
export interface PolygonEditorRenderState {
  mode: EditorMode;
  /** Committed vertices in SCREEN space. */
  screenPoints: ScreenPoint[];
  /** The in-progress draw's dropped vertices (screen space), or null. */
  draftScreenPoints: ScreenPoint[] | null;
  /** The rubber-band cursor point (screen) during draw, or null. */
  rubberBand: ScreenPoint | null;
  /** Index of the focused/selected vertex, or null. */
  activeVertex: number | null;
  /** Whether the shape is closed (fill) or open (stroke only). */
  closed: boolean;
  /** True while the user is mid-draw (no committed shape yet). */
  drawing: boolean;
}

export interface PolygonEditorApi {
  /** Render state for the Konva layer. */
  render: PolygonEditorRenderState;
  // ----- pointer interactions (screen-space) -----
  /** A click on the surface: drop a draw vertex, or close/finish, or hit-test
   *  an existing vertex/edge for select/insert. */
  handleSurfaceClick(screen: ScreenPoint): void;
  /** A double-click: finish a polyline draw (stays open). */
  handleSurfaceDoubleClick(screen: ScreenPoint): void;
  /** Pointer move while drawing → updates the rubber-band preview. */
  handlePointerMove(screen: ScreenPoint): void;
  /** Begin dragging a vertex/corner; returns the grabbed index or null. */
  beginVertexDrag(screen: ScreenPoint): number | null;
  /** Continue an in-progress vertex/corner drag. */
  dragVertexTo(screen: ScreenPoint): void;
  /** End a vertex/corner drag (commits the move onto the undo stack). */
  endVertexDrag(): void;
  // ----- keyboard interactions -----
  /** Select a vertex by index (for keyboard traversal / focus ring). */
  selectVertex(index: number | null): void;
  /** Move focus to the next/previous vertex (a11y traversal). */
  focusNextVertex(direction: 1 | -1): void;
  /** Arrow-key nudge the active vertex by `dxScreen,dyScreen` CSS px. */
  nudgeActiveVertex(dxScreen: number, dyScreen: number): void;
  /** Delete the active vertex (enforces min; returns false on no-op). */
  deleteActiveVertex(): boolean;
  /** Close a polygon / finish a polyline draw (Enter). Returns true if it did. */
  closeOrFinishDraw(): boolean;
  /** Esc — cancel an in-progress draw (or clear selection). */
  cancel(): void;
  // ----- undo API (host-drivable — GEOM-02 AC5) -----
  /** Undo the last per-action change. Returns true if something was undone. */
  undo(): boolean;
  /** Whether an undo is currently available. */
  canUndo: boolean;
  /** Explicit host Save → fires `onCommit` with the current committed value. */
  commit(): void;
}

interface DraftState {
  /** Vertices dropped so far in the current draw (normalised). */
  points: Point[];
}

export function usePolygonEditor(
  options: UsePolygonEditorOptions,
): PolygonEditorApi {
  const { value, transform, onChange, onCommit, editable = true } = options;
  const { onInvalidGeometry } = options;

  // Per-action undo stack of committed VALUES (snapshots). The host can also
  // drive `undo()` from its own stack (GEOM-02 story 064 integration).
  const undoStackRef = useRef<PolygonValue[]>([]);
  const [canUndo, setCanUndo] = useState(false);

  // In-progress draw (null when not drawing). A draw starts when the incoming
  // `value` has no vertices and the user clicks the surface.
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [rubberBand, setRubberBand] = useState<ScreenPoint | null>(null);
  const [activeVertex, setActiveVertex] = useState<number | null>(null);

  // Live drag bookkeeping. `dragIndex` is the vertex being moved; we snapshot
  // the pre-drag value so a drag is ONE undo step (not one per mousemove).
  const dragIndexRef = useRef<number | null>(null);
  const dragSnapshotRef = useRef<PolygonValue | null>(null);

  // `transform.version` is intentionally in the dep list so the projection
  // callbacks are recreated whenever the host pans/zooms (GEOM-02 AC3).
  const toImage = useCallback(
    (s: ScreenPoint): Point => transform.screenToImage(s),
    [transform, transform.version],
  );
  const toScreen = useCallback(
    (p: Point): ScreenPoint => transform.imageToScreen(p),
    [transform, transform.version],
  );

  const pushUndo = useCallback((snapshot: PolygonValue) => {
    undoStackRef.current.push({
      mode: snapshot.mode,
      points: snapshot.points.map((p) => ({ ...p })),
    });
    setCanUndo(true);
  }, []);

  /** Emit a new committed value (records undo + fires onChange). `commitToHost`
   *  additionally fires `onCommit`. */
  const applyChange = useCallback(
    (next: PolygonValue, opts?: { commitToHost?: boolean }) => {
      pushUndo(value);
      onChange?.(next);
      if (opts?.commitToHost) onCommit?.(next);
    },
    [onChange, onCommit, pushUndo, value],
  );

  // ------------------------------------------------------------------ drawing
  const isEmpty = value.points.length === 0;

  const handleSurfaceClick = useCallback(
    (screen: ScreenPoint) => {
      if (!editable) return;
      const img = toImage(screen);

      // (A) DRAW mode: no committed shape yet (or a draw is in progress).
      if (isEmpty || draft) {
        const current = draft?.points ?? [];
        // Close a polygon by clicking near the first vertex.
        const first = current[0];
        if (
          first &&
          isClosedMode(value.mode) &&
          current.length >= minVertices(value.mode)
        ) {
          const firstScreen = toScreen(first);
          if (withinPx(screen, firstScreen, CLOSE_SNAP_RADIUS_PX)) {
            finishDraw(current);
            return;
          }
        }
        const next = [...current, img];
        setDraft({ points: next });
        setRubberBand(screen);
        return;
      }

      // (B) EDIT mode: hit-test an existing vertex (select), else an edge
      // (insert a vertex), else clear selection.
      const vi = hitVertex(value.points, screen);
      if (vi !== null) {
        setActiveVertex(vi);
        return;
      }
      const edge = nearestEdge(value.points, img, value.mode);
      const edgeA = edge ? value.points[edge.edgeIndex] : undefined;
      const edgeB = edge
        ? value.points[(edge.edgeIndex + 1) % value.points.length]
        : undefined;
      if (edge && edgeA && edgeB) {
        const edgeAScreen = toScreen(edgeA);
        const edgeBScreen = toScreen(edgeB);
        if (
          withinSegmentPx(screen, edgeAScreen, edgeBScreen, EDGE_HIT_RADIUS_PX)
        ) {
          const next: PolygonValue = {
            mode: value.mode,
            points: insertVertex(value.points, edge.insertAt, img),
          };
          applyChange(next);
          setActiveVertex(edge.insertAt);
          return;
        }
      }
      setActiveVertex(null);
    },
    [editable, toImage, toScreen, isEmpty, draft, value, applyChange],
  );

  const finishDraw = useCallback(
    (points: Point[]) => {
      // ANA-R4-VALID-01 — refuse a self-intersecting shape (crossing edges).
      // Concave-but-simple shapes are allowed. The draw stays open so the user
      // can fix it; the host surfaces the "crossing lines — prohibited" popup.
      if (isSelfIntersecting(points, isClosedMode(value.mode))) {
        onInvalidGeometry?.("self-intersecting");
        return;
      }
      const committed: PolygonValue = {
        mode: value.mode,
        points: points.map((p) => ({ ...p })),
      };
      setDraft(null);
      setRubberBand(null);
      onChange?.(committed);
      onCommit?.(committed);
      // The committed draw is itself one undo step (undo ⇒ back to empty).
      pushUndo({ mode: value.mode, points: [] });
    },
    [value.mode, onChange, onCommit, onInvalidGeometry, pushUndo],
  );

  const handleSurfaceDoubleClick = useCallback(
    (_screen: ScreenPoint) => {
      if (!editable) return;
      if (!draft) return;
      if (value.mode !== "polyline") return;
      if (draft.points.length >= minVertices("polyline")) {
        finishDraw(draft.points);
      }
    },
    [editable, draft, value.mode, finishDraw],
  );

  const handlePointerMove = useCallback(
    (screen: ScreenPoint) => {
      if (draft) setRubberBand(screen);
    },
    [draft],
  );

  // -------------------------------------------------------------- vertex drag
  const beginVertexDrag = useCallback(
    (screen: ScreenPoint): number | null => {
      if (!editable || draft) return null;
      const vi = hitVertex(value.points, screen);
      if (vi === null) return null;
      dragIndexRef.current = vi;
      dragSnapshotRef.current = {
        mode: value.mode,
        points: value.points.map((p) => ({ ...p })),
      };
      setActiveVertex(vi);
      return vi;
    },
    [editable, draft, value],
  );

  const dragVertexTo = useCallback(
    (screen: ScreenPoint) => {
      const idx = dragIndexRef.current;
      if (idx === null) return;
      const img = toImage(screen);
      let nextPoints: Point[];
      if (value.mode === "rect") {
        nextPoints = resizeRect(value.points, idx, img);
      } else {
        nextPoints = moveVertex(value.points, idx, img);
      }
      // Live preview only — no undo step yet (committed on endVertexDrag).
      onChange?.({ mode: value.mode, points: nextPoints });
    },
    [toImage, value, onChange],
  );

  const endVertexDrag = useCallback(() => {
    if (dragIndexRef.current === null) return;
    const snapshot = dragSnapshotRef.current;
    dragIndexRef.current = null;
    dragSnapshotRef.current = null;
    // One undo step for the whole drag: push the pre-drag snapshot.
    if (snapshot) pushUndo(snapshot);
    onCommit?.(value);
  }, [pushUndo, onCommit, value]);

  // ------------------------------------------------------------------ keyboard
  const selectVertex = useCallback((index: number | null) => {
    setActiveVertex(index);
  }, []);

  const focusNextVertex = useCallback(
    (direction: 1 | -1) => {
      const n = value.points.length;
      if (n === 0) return;
      setActiveVertex((cur) => {
        const start = cur ?? (direction === 1 ? -1 : 0);
        return (((start + direction) % n) + n) % n;
      });
    },
    [value.points.length],
  );

  const nudgeActiveVertex = useCallback(
    (dxScreen: number, dyScreen: number) => {
      if (activeVertex === null) return;
      const cur = value.points[activeVertex];
      if (!cur) return;
      const curScreen = toScreen(cur);
      const nextImg = toImage({
        x: curScreen.x + dxScreen,
        y: curScreen.y + dyScreen,
      });
      const next: PolygonValue =
        value.mode === "rect"
          ? {
              mode: "rect",
              points: resizeRect(value.points, activeVertex, nextImg),
            }
          : {
              mode: value.mode,
              points: moveVertex(value.points, activeVertex, nextImg),
            };
      applyChange(next);
    },
    [activeVertex, value, toScreen, toImage, applyChange],
  );

  const deleteActiveVertex = useCallback((): boolean => {
    if (activeVertex === null) return false;
    if (value.mode === "rect") return false; // a rect has exactly 4 corners
    const { points, removed } = removeVertex(
      value.points,
      activeVertex,
      value.mode,
    );
    if (!removed) return false;
    applyChange({ mode: value.mode, points });
    setActiveVertex(null);
    return true;
  }, [activeVertex, value, applyChange]);

  const closeOrFinishDraw = useCallback((): boolean => {
    if (!draft) return false;
    const min = minVertices(value.mode);
    if (draft.points.length < min) return false;
    finishDraw(draft.points);
    return true;
  }, [draft, value.mode, finishDraw]);

  const cancel = useCallback(() => {
    if (draft) {
      setDraft(null);
      setRubberBand(null);
      return;
    }
    setActiveVertex(null);
  }, [draft]);

  // ---------------------------------------------------------------------- undo
  const undo = useCallback((): boolean => {
    const prev = undoStackRef.current.pop();
    setCanUndo(undoStackRef.current.length > 0);
    if (!prev) return false;
    // Undoing into an empty value drops any in-progress draft too.
    setDraft(null);
    setRubberBand(null);
    onChange?.(prev);
    return true;
  }, [onChange]);

  const commit = useCallback(() => {
    onCommit?.(value);
  }, [onCommit, value]);

  // --------------------------------------------------------------- render state
  const render = useMemo<PolygonEditorRenderState>(() => {
    const screenPoints = value.points.map(toScreen);
    const draftScreenPoints = draft ? draft.points.map(toScreen) : null;
    return {
      mode: value.mode,
      screenPoints,
      draftScreenPoints,
      rubberBand,
      activeVertex,
      closed: isClosedMode(value.mode),
      drawing: draft !== null,
    };
  }, [value, draft, rubberBand, activeVertex, toScreen]);

  // --------------------------------------------------------- screen hit-testing
  function hitVertex(
    points: readonly Point[],
    screen: ScreenPoint,
  ): number | null {
    let best: { i: number; d: number } | null = null;
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      if (!p) continue;
      const ps = toScreen(p);
      const d = screenDistSq(screen, ps);
      if (d <= HANDLE_HIT_RADIUS_PX * HANDLE_HIT_RADIUS_PX) {
        if (best === null || d < best.d) best = { i, d };
      }
    }
    return best?.i ?? null;
  }

  return {
    render,
    handleSurfaceClick,
    handleSurfaceDoubleClick,
    handlePointerMove,
    beginVertexDrag,
    dragVertexTo,
    endVertexDrag,
    selectVertex,
    focusNextVertex,
    nudgeActiveVertex,
    deleteActiveVertex,
    closeOrFinishDraw,
    cancel,
    undo,
    canUndo,
    commit,
  };
}

// --------------------------------------------------------------------- helpers
function screenDistSq(a: ScreenPoint, b: ScreenPoint): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function withinPx(a: ScreenPoint, b: ScreenPoint, px: number): boolean {
  return screenDistSq(a, b) <= px * px;
}

function withinSegmentPx(
  p: ScreenPoint,
  a: ScreenPoint,
  b: ScreenPoint,
  px: number,
): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return withinPx(p, a, px);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * dx;
  const cy = a.y + t * dy;
  const ex = p.x - cx;
  const ey = p.y - cy;
  return ex * ex + ey * ey <= px * px;
}

// re-export for tests / consumers
export { distanceSq };
